import {Button, Form, Modal} from "react-bootstrap";
import React, {useState, useEffect, useCallback} from "react";
import {useAuth} from "../contexts/AuthContext";
import AuthClientFactory from "../api/rest/auth/AuthClientFactory";

type SigninModalProps = {
    show: boolean;
    onClose: () => void;
};

export const SigninModal = ({show, onClose}: SigninModalProps) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [provider, setProvider] = useState<string | null | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    
    const authClient = AuthClientFactory.getAuthClient();
    const {refreshSession, user} = useAuth();

    const handleClose = useCallback(() => {
        setEmail("");
        setPassword("");
        setProvider(undefined);
        onClose();
    }, [onClose]);

    // Zamknij modal gdy użytkownik się zaloguje (np. przez OAuth)
    useEffect(() => {
        if (user && show) {
            handleClose();
        }
    }, [user, show, handleClose]);

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        
        try {
            const data = await authClient.checkProvider({ email });
            if (data.provider !== null) {
                // Jeśli użytkownik ma providera, automatycznie inicjuj OAuth
                authClient.initiateOAuthSignin(data.provider);
                // Zamykamy modal od razu, ponieważ OAuth flow będzie obsłużony
                handleClose();
                // For mock client, wait a bit then refresh session
                // For real OAuth, session will be set after redirect
                setTimeout(async () => {
                    await refreshSession();
                }, 1000);
            } else {
                // Jeśli provider jest null, pokaż pole hasła
                setProvider(data.provider);
            }
        } catch (error) {
            console.error('Error checking provider:', error);
            // TODO: Handle error (show error message to user)
        } finally {
            setIsLoading(false);
        }
    };

    const handleSignInSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            await authClient.signin({ email, password });
            await refreshSession();
            handleClose();
        } catch (error) {
            console.error('Error signing in:', error);
            // TODO: Handle error (show error message to user)
        } finally {
            setIsLoading(false);
        }
    };


    const handleGoogleSignin = async () => {
        authClient.initiateOAuthSignin('google');
        // For mock client, wait a bit then refresh session
        // For real OAuth, session will be set after redirect
        setTimeout(async () => {
            await refreshSession();
        }, 1000);
    };

    const handleGithubSignin = async () => {
        authClient.initiateOAuthSignin('github');
        // For mock client, wait a bit then refresh session
        // For real OAuth, session will be set after redirect
        setTimeout(async () => {
            await refreshSession();
        }, 1000);
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Sign in</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={provider === null ? handleSignInSubmit : handleEmailSubmit}>
                    <Form.Group className="mb-3" controlId="formEmail">
                        <Form.Label>Email address</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder="Enter email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={isLoading}
                        />
                    </Form.Group>
                    {provider === null && (
                        <Form.Group className="mb-3" controlId="formPassword">
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="Enter password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={isLoading}
                            />
                        </Form.Group>
                    )}
                    <Button variant="primary" type="submit" className="w-100" disabled={isLoading}>
                        {provider === null ? 'Sign In' : 'Continue'}
                    </Button>
                </Form>

                <hr className="my-4" />
                <div className="text-center text-muted mb-3">or signin with</div>
                
                <div className="d-grid gap-2">
                    <Button 
                        variant="outline-secondary" 
                        onClick={handleGoogleSignin}
                        className="d-flex align-items-center justify-content-center"
                    >
                        <svg className="me-2" width="18" height="18" viewBox="0 0 18 18">
                            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                            <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.348 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
                            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
                        </svg>
                        Google
                    </Button>
                    <Button 
                        variant="outline-secondary" 
                        onClick={handleGithubSignin}
                        className="d-flex align-items-center justify-content-center"
                    >
                        <svg className="me-2" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.012 8.012 0 0 0 16 8c0-4.42-3.58-8-8-8z"/>
                        </svg>
                        Github
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

